import React, { useEffect, useState, useMemo } from 'react';
import { Search, Grid3X3, LayoutGrid, Package, Sparkles, SlidersHorizontal, X, ChevronDown, ArrowUpDown, Filter, ChevronRight } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

const SORT_OPTIONS = [
  { value: 'featured', label: 'Destaques' },
  { value: 'price_asc', label: 'Menor preço' },
  { value: 'price_desc', label: 'Maior preço' },
  { value: 'name_asc', label: 'Nome A-Z' },
  { value: 'name_desc', label: 'Nome Z-A' },
  { value: 'newest', label: 'Mais recentes' },
];

const PRICE_RANGES = [
  { value: 'all', label: 'Todos os preços', min: 0, max: Infinity },
  { value: '0-100', label: 'Até R$ 100', min: 0, max: 100 },
  { value: '100-500', label: 'R$ 100 - R$ 500', min: 100, max: 500 },
  { value: '500-1000', label: 'R$ 500 - R$ 1.000', min: 500, max: 1000 },
  { value: '1000-5000', label: 'R$ 1.000 - R$ 5.000', min: 1000, max: 5000 },
  { value: '5000+', label: 'Acima de R$ 5.000', min: 5000, max: Infinity },
];

export const Shop = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [priceRange, setPriceRange] = useState('all');
  const [showOnlyInStock, setShowOnlyInStock] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gridCols, setGridCols] = useState(3);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchAllProducts();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      if (!Array.isArray(response.data)) {
        console.error('Categorias: resposta inesperada (não é array):', response.data);
        setCategories([]);
        return;
      }
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchAllProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`);
      if (!Array.isArray(response.data)) {
        console.error('Produtos: resposta inesperada (não é array):', response.data);
        setAllProducts([]);
        return;
      }
      setAllProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Contagem de produtos por categoria
  const categoryCounts = useMemo(() => {
    const counts = { all: allProducts.length };
    if (!Array.isArray(categories)) {
      return counts;
    }
    categories.forEach(cat => {
      counts[cat.id] = allProducts.filter(p => p.category_id === cat.id).length;
    });
    return counts;
  }, [allProducts, categories]);

  // Filtrar e ordenar produtos
  const filteredProducts = useMemo(() => {
    let result = [...allProducts];

    // Filtro por categoria
    if (selectedCategory !== 'all') {
      result = result.filter(p => p.category_id === selectedCategory);
    }

    // Filtro por busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(term) || 
        p.description?.toLowerCase().includes(term)
      );
    }

    // Filtro por faixa de preço
    if (priceRange !== 'all') {
      const range = PRICE_RANGES.find(r => r.value === priceRange);
      if (range) {
        result = result.filter(p => p.price >= range.min && p.price < range.max);
      }
    }

    // Filtro por estoque
    if (showOnlyInStock) {
      result = result.filter(p => p.stock > 0);
    }

    // Ordenação
    switch (sortBy) {
      case 'price_asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price_desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name_asc':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name_desc':
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'featured':
      default:
        result.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        break;
    }

    return result;
  }, [allProducts, selectedCategory, searchTerm, priceRange, showOnlyInStock, sortBy]);

  const selectedCategoryName = categories.find(c => c.id === selectedCategory)?.name;
  const selectedPriceLabel = PRICE_RANGES.find(r => r.value === priceRange)?.label;

  const clearAllFilters = () => {
    setSelectedCategory('all');
    setSearchTerm('');
    setPriceRange('all');
    setShowOnlyInStock(false);
    setSortBy('featured');
  };

  const hasActiveFilters = selectedCategory !== 'all' || searchTerm || priceRange !== 'all' || showOnlyInStock;

  // Sidebar de filtros (desktop e mobile)
  const FiltersContent = () => (
    <div className="space-y-6">
      {/* Categorias */}
      <div>
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <div className="p-1.5 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
            <Package className="h-4 w-4 text-white" />
          </div>
          Categorias
        </h3>
        <div className="space-y-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`w-full text-left px-3 py-2.5 rounded-xl transition-all duration-200 flex items-center justify-between ${
              selectedCategory === 'all' 
                ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 dark:from-cyan-500/20 dark:to-blue-500/20 text-cyan-600 dark:text-cyan-400 font-medium border border-cyan-500/30' 
                : 'hover:bg-slate-100 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-300 border border-transparent'
            }`}
          >
            <span>Todas</span>
            <Badge variant="secondary" className="text-xs">{categoryCounts.all || 0}</Badge>
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`w-full text-left px-3 py-2.5 rounded-xl transition-all duration-200 flex items-center justify-between ${
                selectedCategory === cat.id 
                  ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 dark:from-cyan-500/20 dark:to-blue-500/20 text-cyan-600 dark:text-cyan-400 font-medium border border-cyan-500/30' 
                  : 'hover:bg-slate-100 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-300 border border-transparent'
              }`}
            >
              <span className="truncate">{cat.name}</span>
              <Badge variant="secondary" className="text-xs ml-2">{categoryCounts[cat.id] || 0}</Badge>
            </button>
          ))}
        </div>
      </div>

      {/* Faixa de Preço */}
      <div>
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <div className="p-1.5 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
            <span className="text-sm font-bold text-white">R$</span>
          </div>
          Faixa de Preço
        </h3>
        <div className="space-y-2">
          {PRICE_RANGES.map(range => (
            <button
              key={range.value}
              onClick={() => setPriceRange(range.value)}
              className={`w-full text-left px-3 py-2.5 rounded-xl transition-all duration-200 ${
                priceRange === range.value 
                  ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 dark:from-cyan-500/20 dark:to-blue-500/20 text-cyan-600 dark:text-cyan-400 font-medium border border-cyan-500/30' 
                  : 'hover:bg-slate-100 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-300 border border-transparent'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Disponibilidade */}
      <div>
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <div className="p-1.5 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
            <div className="w-4 h-4 flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
          </div>
          Disponibilidade
        </h3>
        <label className="flex items-center gap-3 cursor-pointer px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
          <Checkbox 
            checked={showOnlyInStock} 
            onCheckedChange={setShowOnlyInStock}
            className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-cyan-500 data-[state=checked]:to-blue-600 data-[state=checked]:border-cyan-500"
          />
          <span className="text-slate-700 dark:text-slate-300">Apenas em estoque</span>
        </label>
      </div>

      {/* Limpar filtros */}
      {hasActiveFilters && (
        <Button 
          variant="outline" 
          className="w-full border-cyan-500/30 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500/50 transition-all" 
          onClick={clearAllFilters}
        >
          <X className="h-4 w-4 mr-2" />
          Limpar todos os filtros
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] pt-24 pb-12">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-[#0f172a] via-blue-900 to-[#0f172a] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 py-16 relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
              <Package className="h-6 w-6 text-white" />
            </div>
            <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30">
              <Sparkles className="h-3 w-3 mr-1" />
              Catálogo Completo
            </Badge>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Nossa Loja</h1>
          <p className="text-lg text-slate-300 max-w-2xl">
            Encontre os melhores dutos e exaustores para sua indústria. Qualidade garantida e entrega para todo o Brasil.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-8 relative z-20">
        {/* Search Bar */}
        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Buscar produtos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-xl text-lg"
                data-testid="search-input"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Mobile Filter Button */}
            <Button 
              variant="outline" 
              className="lg:hidden w-full md:w-auto h-12 rounded-xl"
              onClick={() => setShowMobileFilters(true)}
            >
              <Filter className="h-5 w-5 mr-2" />
              Filtros
              {hasActiveFilters && (
                <Badge className="ml-2 bg-blue-600 text-white">!</Badge>
              )}
            </Button>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-[200px] h-12 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-xl">
                <ArrowUpDown className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {SORT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Grid Toggle */}
            <div className="hidden lg:flex items-center gap-1 bg-slate-100 dark:bg-slate-900 p-1 rounded-xl">
              <button
                onClick={() => setGridCols(2)}
                className={`p-2 rounded-lg transition-colors ${gridCols === 2 ? 'bg-white dark:bg-slate-700 shadow-sm' : 'hover:bg-white/50'}`}
              >
                <LayoutGrid className="h-5 w-5 text-slate-600 dark:text-slate-300" />
              </button>
              <button
                onClick={() => setGridCols(3)}
                className={`p-2 rounded-lg transition-colors ${gridCols === 3 ? 'bg-white dark:bg-slate-700 shadow-sm' : 'hover:bg-white/50'}`}
              >
                <Grid3X3 className="h-5 w-5 text-slate-600 dark:text-slate-300" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content with Sidebar */}
        <div className="flex gap-8">
          {/* Sidebar - Desktop */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="bg-white dark:bg-gradient-to-b dark:from-slate-800 dark:to-[#0f172a] p-6 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700/50 sticky top-28">
              <h2 className="font-bold text-lg text-slate-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl shadow-lg shadow-cyan-500/20">
                  <SlidersHorizontal className="h-5 w-5 text-white" />
                </div>
                Filtros
              </h2>
              <FiltersContent />
            </div>
          </aside>

          {/* Products Area */}
          <div className="flex-1 min-w-0">
            {/* Active Filters */}
            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2 mb-6 p-4 bg-white dark:bg-gradient-to-r dark:from-slate-800 dark:to-[#0f172a] rounded-xl border border-slate-200 dark:border-cyan-500/20">
                <span className="text-sm text-slate-500 dark:text-cyan-400 font-medium">Filtros:</span>
                {selectedCategory !== 'all' && (
                  <Badge className="gap-1 px-3 py-1.5 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 dark:from-cyan-500/20 dark:to-blue-500/20 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30 hover:border-cyan-500/50">
                    {selectedCategoryName}
                    <button onClick={() => setSelectedCategory('all')} className="ml-1 hover:text-red-500 transition-colors">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {priceRange !== 'all' && (
                  <Badge className="gap-1 px-3 py-1.5 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 dark:from-cyan-500/20 dark:to-blue-500/20 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30 hover:border-cyan-500/50">
                    {selectedPriceLabel}
                    <button onClick={() => setPriceRange('all')} className="ml-1 hover:text-red-500 transition-colors">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {showOnlyInStock && (
                  <Badge className="gap-1 px-3 py-1.5 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 dark:from-cyan-500/20 dark:to-blue-500/20 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30 hover:border-cyan-500/50">
                    Em estoque
                    <button onClick={() => setShowOnlyInStock(false)} className="ml-1 hover:text-red-500 transition-colors">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {searchTerm && (
                  <Badge className="gap-1 px-3 py-1.5 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 dark:from-cyan-500/20 dark:to-blue-500/20 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30 hover:border-cyan-500/50">
                    "{searchTerm}"
                    <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-red-500 transition-colors">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                <button 
                  onClick={clearAllFilters}
                  className="text-sm text-cyan-600 hover:text-cyan-500 dark:text-cyan-400 dark:hover:text-cyan-300 font-medium ml-auto transition-colors"
                >
                  Limpar todos
                </button>
              </div>
            )}

            {/* Results Count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-slate-600 dark:text-slate-400">
                {loading ? 'Carregando...' : (
                  <>
                    <span className="font-semibold text-slate-900 dark:text-white">{filteredProducts.length}</span>
                    {' '}produto{filteredProducts.length !== 1 ? 's' : ''} encontrado{filteredProducts.length !== 1 ? 's' : ''}
                  </>
                )}
              </p>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl overflow-hidden animate-pulse">
                    <div className="aspect-square bg-slate-200 dark:bg-slate-700"></div>
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div>
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
                      <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredProducts.length > 0 ? (
              <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols === 3 ? 'xl:grid-cols-3' : 'xl:grid-cols-2'} gap-6`} data-testid="products-grid">
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
                <div className="w-24 h-24 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Package className="h-12 w-12 text-slate-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Nenhum produto encontrado</h3>
                <p className="text-slate-600 dark:text-slate-400 mb-6">Tente ajustar os filtros ou buscar por outro termo</p>
                <Button onClick={clearAllFilters}>
                  Limpar Filtros
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-[#0f172a]/70 backdrop-blur-sm" onClick={() => setShowMobileFilters(false)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-full bg-white dark:bg-gradient-to-b dark:from-slate-800 dark:to-[#0f172a] shadow-2xl shadow-cyan-500/10 overflow-y-auto border-l border-slate-200 dark:border-cyan-500/20">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-bold text-lg text-slate-900 dark:text-white flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl shadow-lg shadow-cyan-500/20">
                    <SlidersHorizontal className="h-5 w-5 text-white" />
                  </div>
                  Filtros
                </h2>
                <button 
                  onClick={() => setShowMobileFilters(false)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl border border-transparent hover:border-cyan-500/30 transition-all"
                >
                  <X className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                </button>
              </div>
              <FiltersContent />
              <div className="mt-6 pt-6 border-t border-slate-200 dark:border-cyan-500/20">
                <Button 
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg shadow-cyan-500/30 font-semibold" 
                  onClick={() => setShowMobileFilters(false)}
                >
                  Ver {filteredProducts.length} produto{filteredProducts.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};